import { defineStore } from 'pinia';
import { useApiNotifications } from '~/composables/api/notifications';
import { useClient } from '~/stores/client';
import type { Notification } from '~/entities/notification';

export function normalizeNotificationDates(items: Notification[]): Notification[] {
  return items.map((item) => {
    const created = item.created as unknown as number | string | Date;
    if (created instanceof Date) {
      item.created = created;
    } else if (typeof created === 'number') {
      item.created = new Date(created > 1e12 ? created : created * 1000);
    } else {
      item.created = new Date(created);
    }
    return item;
  });
}

type PagePayload = {
  items: Notification[];
  total: number;
  newCount: number;
};

async function fetchNotificationsPage(clientId: string, page: number, perPage: number): Promise<PagePayload> {
  const api = useApiNotifications(clientId);
  const response = await api.get(page, perPage);
  if (response.error.value) {
    throw new Error(apiErrorMessage(response.error.value, 'Failed to load notifications'));
  }

  const payload = response.data.value;
  if (!payload?.notifications) {
    throw new Error('Failed to load notifications');
  }

  return {
    items: normalizeNotificationDates(payload.notifications.items ?? []),
    total: payload.notifications.count ?? 0,
    newCount: payload.notifications.newCount ?? 0,
  };
}

// Client-only in-flight dedup for the first page (profile preview +
// notifications page racing on login redirect). Module-level on purpose,
// but never touched on the server: concurrent SSR requests must not share
// a promise bound to another request's store instance.
let firstPageRequest: { clientId: string, promise: Promise<PagePayload> } | null = null;

export const useNotifications = defineStore('notifications', {
  state: () => ({
    preview: [] as Notification[],
    previewLoadedAt: 0,
    pageItems: [] as Notification[],
    // Counters owned exclusively by REST responses; realtime pushIncoming
    // must not touch them or hasMore diverges from what the server reports.
    restTotal: 0,
    restLoaded: 0,
    total: 0,
    newCount: 0,
    page: 1,
    loadedKey: null as string | null,
    loadedPerPage: 10,
    loading: false,
    loadError: false,
    // Bumped on resetAll/logout: every async writer checks its snapshot
    // against the current value before mutating state, so stale responses
    // from a previous session/user can never repopulate the store.
    generation: 0,
    // Realtime rows (and their ids) that arrived before any REST page
    // covered them. Kept separately from pageItems so pushIncoming works
    // even when pageItems is still empty (realtime before first load):
    // applyFirstPage merges them back until REST reports the same id.
    realtimeIds: [] as string[],
    realtimeItems: [] as Notification[],
  }),

  getters: {
    hasMore(): boolean {
      return this.restLoaded < this.restTotal;
    },
  },

  actions: {
    markViewedLocal(ids: string[]): number {
      let marked = 0;
      for (const id of ids) {
        const item = this.pageItems.find(n => n.id === id);
        if (item && !item.isViewed) {
          item.isViewed = true;
          marked++;
        }
        const previewItem = this.preview.find(n => n.id === id);
        if (previewItem && !previewItem.isViewed) {
          previewItem.isViewed = true;
        }
      }
      if (marked > 0) {
        this.newCount = Math.max(0, this.newCount - marked);
      }
      return marked;
    },

    syncPreview(items: Notification[], newCount: number): void {
      this.preview = items.slice(0, 3);
      this.previewLoadedAt = Date.now();
      this.newCount = newCount;
    },

    resetAll(): void {
      this.generation += 1;
      this.$reset();
    },

    rememberRealtime(notification: Notification): void {
      if (!this.realtimeIds.includes(notification.id)) {
        this.realtimeIds.push(notification.id);
        this.realtimeItems.unshift(notification);
      } else {
        const index = this.realtimeItems.findIndex(n => n.id === notification.id);
        if (index !== -1) {
          this.realtimeItems.splice(index, 1, notification);
        }
      }
    },

    pushIncoming(notification: Notification): void {
      const alreadyKnown = this.preview.some(n => n.id === notification.id)
        || this.pageItems.some(n => n.id === notification.id);
      const isNewUnread = !alreadyKnown && !notification.isViewed;
      const preview = [notification, ...this.preview.filter(n => n.id !== notification.id)].slice(0, 3);
      this.syncPreview(preview, isNewUnread ? this.newCount + 1 : this.newCount);
      // REST pagination owns `restTotal`/`restLoaded`; realtime items join
      // the visible list but must not move those counters, otherwise
      // hasMore never converges once the server includes the item.
      // Tracked separately from pageItems so realtime arriving before the
      // first REST load is not lost when applyFirstPage replaces preview.
      this.rememberRealtime(notification);
      if (!this.pageItems.some(n => n.id === notification.id)) {
        this.pageItems.unshift(notification);
      }
    },

    hasFreshFirstPage(clientId: string, perPage: number): boolean {
      return this.loadedKey === clientId && this.pageItems.length > 0 && this.loadedPerPage === perPage;
    },

    isPageLoaded(clientId: string | null, page: number, perPage: number): boolean {
      if (!clientId || this.loadedKey !== clientId || this.loadedPerPage !== perPage) {
        return false;
      }
      return page <= this.page;
    },

    async refreshAfterAction(perPage = 10): Promise<void> {
      const clientId = useClient().getClientId;
      if (!clientId) {
        return;
      }
      const generation = this.generation;
      this.loading = true;
      this.loadError = false;
      try {
        // Bypass ensureFirstPage cache: the action may have changed server state.
        // A fresh refresh drops accumulated pages — realtime rows survive
        // via realtimeIds, everything else is replaced by page 1.
        const liveIds = new Set(this.pageItems.map(n => n.id));
        this.realtimeIds = this.realtimeIds.filter(id => liveIds.has(id));
        this.realtimeItems = this.realtimeItems.filter(n => liveIds.has(n.id));
        const { items, total, newCount } = await fetchNotificationsPage(clientId, 1, perPage);
        if (generation !== this.generation || useClient().getClientId !== clientId) {
          return;
        }
        this.applyFirstPage(clientId, perPage, items, total, newCount);
      } catch {
        if (generation === this.generation) {
          this.loadError = true;
        }
      } finally {
        if (generation === this.generation) {
          this.loading = false;
        }
      }
    },

    async ensureFirstPage(clientId: string, perPage = 10): Promise<void> {
      if (this.hasFreshFirstPage(clientId, perPage)) {
        this.syncPreview(this.pageItems, this.newCount);
        return;
      }
      // Avoid hammering the API when the preview was just loaded elsewhere.
      if (Date.now() - this.previewLoadedAt < 30_000 && this.preview.length > 0 && this.loadedKey === clientId) {
        return;
      }
      // Client-only request sharing: on the server each request populates
      // its own store instance, so no cross-request promise sharing.
      if (import.meta.server) {
        const generation = this.generation;
        const { items, total, newCount } = await fetchNotificationsPage(clientId, 1, perPage);
        if (generation !== this.generation) {
          return;
        }
        this.applyFirstPage(clientId, perPage, items, total, newCount);
        return;
      }
      const generation = this.generation;
      if (!firstPageRequest || firstPageRequest.clientId !== clientId) {
        firstPageRequest = {
          clientId,
          promise: fetchNotificationsPage(clientId, 1, perPage).finally(() => {
            if (firstPageRequest?.clientId === clientId) {
              firstPageRequest = null;
            }
          }),
        };
      }
      const { items, total, newCount } = await firstPageRequest.promise;
      // Awaiters share the payload but write it into their own store instance.
      // Drop the result if logout/reset happened mid-flight or the user changed.
      if (generation !== this.generation || useClient().getClientId !== clientId) {
        return;
      }
      this.applyFirstPage(clientId, perPage, items, total, newCount);
    },

    applyFirstPage(clientId: string, perPage: number, items: Notification[], total: number, newCount: number): void {
      // Preserve only realtime rows (tracked by id): anything else not in
      // the fresh response is stale REST data from a previous pagination
      // state and must not be resurrected after refresh. Realtime rows are
      // read from the dedicated buffer, so they survive even when pageItems
      // was empty at pushIncoming time.
      const freshIds = new Set(items.map(n => n.id));
      const keptRealtime = this.realtimeItems.filter(n => !freshIds.has(n.id));
      this.realtimeItems = keptRealtime;
      this.realtimeIds = keptRealtime.map(n => n.id);
      this.pageItems = [...keptRealtime, ...items];
      this.restTotal = total;
      this.restLoaded = items.length;
      this.total = total;
      this.page = 1;
      this.loadedKey = clientId;
      this.loadedPerPage = perPage;
      this.syncPreview(items, newCount);
    },

    async loadPreview(): Promise<void> {
      const clientId = useClient().getClientId;
      if (!clientId) {
        return;
      }
      try {
        await this.ensureFirstPage(clientId, 10);
      } catch {
        // Preview is best-effort; the page surface shows its own error state.
        // ensureFirstPage guards staleness internally via generation checks.
      }
    },

    async loadPage(page: number, perPage = 10, append = false): Promise<void> {
      const clientId = useClient().getClientId;
      if (!clientId) {
        return;
      }
      if (append) {
        // Guard against double-append (watcher + explicit call racing):
        // only fetch when the store does not already cover the page.
        if (page <= this.page && this.loadedKey === clientId && this.loadedPerPage === perPage) {
          return;
        }
        const generation = this.generation;
        const { items, total } = await fetchNotificationsPage(clientId, page, perPage);
        if (generation !== this.generation || useClient().getClientId !== clientId) {
          return;
        }
        const known = new Set(this.pageItems.map(n => n.id));
        const fresh = items.filter(n => !known.has(n.id));
        this.pageItems.push(...fresh);
        this.restTotal = total;
        // Coverage counts every REST row of the fetched page, including
        // realtime rows the server already reports: hasMore must converge
        // to the server's total once the last page is fetched.
        this.restLoaded += items.length;
        this.total = total;
        this.page = page;
        return;
      }
      if (this.hasFreshFirstPage(clientId, perPage) && page === 1) {
        this.syncPreview(this.pageItems, this.newCount);
        return;
      }
      // Loading flags are owned by the page (fetchPage) so SSR suspense
      // stays in sync; the store only flips loadError here.
      const generation = this.generation;
      try {
        await this.ensureFirstPage(clientId, perPage);
        if (generation !== this.generation || useClient().getClientId !== clientId) {
          return;
        }
        if (page !== 1) {
          // Deep link ?page=N keeps the cumulative list contract: fetch
          // pages 1..N in order and accumulate, so earlier pages are
          // present and restLoaded reflects actually loaded REST rows.
          // Staging starts from a FRESH page 1 (not the accumulated
          // pageItems): re-entering with ?page=2 after pages 1..5 were
          // loaded must not keep stale rows 3..5 nor double-count
          // restLoaded for re-fetched pages.
          // Atomic publish: commit only after all pages succeed; on error
          // the last consistent snapshot stays untouched.
          const first = await fetchNotificationsPage(clientId, 1, perPage);
          if (generation !== this.generation || useClient().getClientId !== clientId) {
            return;
          }
          const stagedItems: Notification[] = [...first.items];
          const stagedKnown = new Set(first.items.map(n => n.id));
          let stagedRestLoaded = first.items.length;
          let stagedRestTotal = first.total;
          let stagedNewCount = first.newCount;
          let stagedTargetItems: Notification[] = first.items;
          for (let p = 2; p <= page; p += 1) {
            const { items, total, newCount } = await fetchNotificationsPage(clientId, p, perPage);
            if (generation !== this.generation || useClient().getClientId !== clientId) {
              return;
            }
            const fresh = items.filter(n => !stagedKnown.has(n.id));
            for (const n of fresh) {
              stagedKnown.add(n.id);
            }
            stagedItems.push(...fresh);
            stagedRestTotal = total;
            stagedRestLoaded += items.length;
            stagedTargetItems = items;
            if (p === page) {
              stagedNewCount = newCount;
            }
          }
          // Merge realtime rows that are still not covered by REST.
          const stagedIds = new Set(stagedItems.map(n => n.id));
          const keptRealtime = this.realtimeItems.filter(n => !stagedIds.has(n.id));
          this.realtimeItems = keptRealtime;
          this.realtimeIds = keptRealtime.map(n => n.id);
          this.pageItems = [...keptRealtime, ...stagedItems];
          this.restTotal = stagedRestTotal;
          this.restLoaded = stagedRestLoaded;
          this.total = stagedRestTotal;
          this.newCount = stagedNewCount;
          this.page = page;
          this.loadedKey = clientId;
          this.loadedPerPage = perPage;
          this.syncPreview(stagedTargetItems, stagedNewCount);
        } else {
          this.page = page;
        }
      } catch {
        if (generation === this.generation) {
          this.loadError = true;
        }
      }
    },
  },
});
